import './i18n';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Home } from './pages/home/Home';
import { Suspense, lazy } from 'react';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { AuthManager } from './components/AuthManager';
import { Layout } from './components/layout/Layout';
import { PrivateRoute } from './components/PrivateRoute';
import { Pricing } from './pages/pricing/Pricing';
import { CanvaLogin } from './pages/canva-login/CanvaLogin';
import { CanvaPricing } from './pages/canva-pricing/CanvaPricing';
import NotFound from './pages/notfound/NotFound';
import { Loader } from './components/Loader';
import { HelmetProvider } from 'react-helmet-async';

function withSuspense<P>(WrappedComponent: any) {
  return (props: P) => (
    <Suspense fallback={<div style={{ padding: '4rem 2rem' }}><Loader /></div>}>
      <WrappedComponent {...props} />
    </Suspense>
  );
}

const Profile = lazy(() => import('./pages/profile/Profile'));
const Checkout = lazy(() => import('./pages/checkout/Checkout'));
const PaymentSuccess = lazy(() => import('./pages/success/PaymentSuccess'));
const Api = lazy(() => import('./pages/api/Api'));
const ApiPricing = lazy(() => import('./pages/api/components/ApiPricing/ApiPricing'));
const ApiDocsV1 = lazy(() => import('./pages/api/components/ApiDocs/ApiDocs_v1'));
const ApiDocsV2 = lazy(() => import('./pages/api/components/ApiDocs/ApiDocs_v2'));
const Affiliate = lazy(() => import('./pages/affiliate/Affiliate'));
const Collection = lazy(() => import('./pages/collection/Collection'));
const Privacy = lazy(() => import('./pages/privacy/Privacy'));
const Terms = lazy(() => import('./pages/terms/Terms'));
const Editor = lazy(() => import('./pages/editor/Editor'));
const Designs = lazy(() => import('./pages/designs/Designs'));
const Orders = lazy(() => import('./pages/orders/Orders'));

const BlogArticle1 = lazy(() => import('./pages/blog/article-1/Article-1'));
const ImageGenerator = lazy(() => import('./pages/image-generator/ImageGenerator'));
const ImageToVector = lazy(() => import('./pages/image-to-vector/ImageToVector'));
const Generate = lazy(() => import('./pages/generate/Generate'));

const SuspendedTerms = withSuspense(Terms);
const SuspendedCollection = withSuspense(Collection);
const SuspendedEditor = withSuspense(Editor);
const SuspendedApiPricing = withSuspense(ApiPricing);
const SuspendedApi = withSuspense(Api);
const SuspendedApiDocsV1 = withSuspense(ApiDocsV1);
const SuspendedApiDocsV2 = withSuspense(ApiDocsV2);
const SuspendedAffiliate = withSuspense(Affiliate);
const SuspendedDesigns = withSuspense(Designs);
const SuspendedOrders = withSuspense(Orders);
const SuspendedProfile = withSuspense(Profile);
const SuspendedPrivacy = withSuspense(Privacy);
const SuspendedCheckout = withSuspense(Checkout);
const SuspendedPaymentSuccess = withSuspense(PaymentSuccess);
const SuspendedBlogArticle1 = withSuspense(BlogArticle1);
const SuspendedImageGenerator = withSuspense(ImageGenerator);
const SuspendedImageToVector = withSuspense(ImageToVector);
const SuspendedGenerate = withSuspense(Generate);

const App = () => {
  return (
    <Provider store={store}>
      <AuthManager>
        <HelmetProvider>
          <Router>
            <Layout>
              <Routes>
                <Route path="/" element={<Navigate to="/en/" replace />} />
                <Route path='/canva-login' element={<CanvaLogin />} />
                <Route path='/:lang/' element={<Home />} />
                <Route path='/:lang/image-generator' element={<SuspendedImageGenerator />} />
                <Route path='/:lang/image-to-vector' element={<SuspendedImageToVector />} />
                <Route path='/:lang/terms' element={<SuspendedTerms />} />
                <Route path='/:lang/privacy' element={<SuspendedPrivacy />} />
                <Route path='/:lang/generate' element={<SuspendedGenerate />} />
                <Route path='/:lang/affiliate' element={<SuspendedAffiliate />} />
                <Route path='/:lang/pricing' element={<Pricing />} />
                <Route path='/:lang/canva-pricing' element={<CanvaPricing />} />
                <Route path='/:lang/payment-success' element={<SuspendedPaymentSuccess />} />
                <Route path='/:lang/api' element={<SuspendedApi />} />
                <Route path='/:lang/api/docs_v2' element={<SuspendedApiDocsV2 />} />
                <Route path='/:lang/api/docs_v1' element={<SuspendedApiDocsV1 />} />
                <Route path='/:lang/api/pricing' element={<SuspendedApiPricing />} />
                <Route path='/:lang/canva-login' element={<CanvaLogin />} />
                <Route path='/blog/designers-what-can-ai-do-for-you' element={<SuspendedBlogArticle1 />} />
                <Route
                  path="/:lang/collection"
                  element={
                    <PrivateRoute>
                      <SuspendedCollection />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/:lang/designs"
                  element={
                    <PrivateRoute>
                      <SuspendedDesigns />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/:lang/editor"
                  element={
                    <PrivateRoute>
                      <SuspendedEditor />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/:lang/profile"
                  element={
                    <PrivateRoute>
                      <SuspendedProfile />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/:lang/profile/orders"
                  element={
                    <PrivateRoute>
                      <SuspendedOrders />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/:lang/checkout"
                  element={<SuspendedCheckout />}
                />
                <Route path='*' element={<NotFound />} />
              </Routes>
            </Layout>
          </Router>
        </HelmetProvider>
      </AuthManager>
    </Provider>
  );
}

export default App;